import Button from '@/components/ui/button/button.component';
import { canAccessDeveloperPage } from '@/core/permissions/permission.core';
import { cn } from '@/core/string.core';
import { usePermissions } from '@/hooks/use-permissions/use-permissions.hook';
import { IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { lazy, Suspense } from 'react';
import { footerTranslations } from './footer.messages';
import { upmVersionTranslations } from '../upm-version/upm-version.messages';

// Dynamic imports to prevent unnecessary component loads when not authenticated
const AppVersion = lazy(() =>
  import('../app-version/app-version.component').then((module) => ({ default: module.AppVersion })),
);

const UpmVersion = lazy(() =>
  import('../upm-version/upm-version.component').then((module) => ({ default: module.UpmVersion })),
);

const footerConfig = (t: IntlShape) => [
  {
    id: 1,
    title: t.formatMessage(footerTranslations.cookieNotice),
    link: 'https://www.siemens.com/cookie-notice',
  },
  {
    id: 2,
    title: t.formatMessage(footerTranslations.privacyNotice),
    link: 'https://www.siemens.com/privacy-notice',
  },
  {
    id: 3,
    title: t.formatMessage(footerTranslations.termsOfUse),
    link: 'https://www.siemens.com/terms-of-use',
  },
  {
    id: 4,
    title: t.formatMessage(footerTranslations.digitalId),
    link: 'https://www.siemens.com/digital-id',
  },
  {
    id: 5,
    title: t.formatMessage(footerTranslations.corporateInformation),
    link: 'https://www.siemens.com/corporate-information',
  },
];

export const Footer = () => {
  const t = useIntl();
  const navigate = useNavigate();
  const { roles, isAuthenticated } = usePermissions();
  let environmentLabel: string | null;
  if (import.meta.env.VITE_ENV === 'dev') {
    environmentLabel = t.formatMessage(footerTranslations.developmentEnvironment);
  } else if (import.meta.env.VITE_ENV === 'test') {
    environmentLabel = t.formatMessage(footerTranslations.testEnvironment);
  } else {
    environmentLabel = null;
  }

  const onLinkClick = (link: string) => {
    window.open(link, '_blank');
  };

  const handleDevPageNav = () => {
    navigate('/dev/test');
  };

  return (
    <div
      id="footer"
      role="contentinfo"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-10',
        'bg-white h-10 px-18',
        'flex flex-row justify-between items-center',
        'border-t border-other-stroke',
      )}
    >
      <div data-testid="footer-link-container" className="flex flex-row justify-between items-center gap-4">
        {footerConfig(t).map((item, index) => (
          <div className="flex flex-row justify-between items-center gap-4" key={item.id}>
            <Button
              role="link"
              variant="ghost"
              data-testid="footer-link"
              onClick={() => onLinkClick(item.link)}
              className="typo-secondary text-secondary-text"
            >
              {item.title}
            </Button>
            {index < footerConfig(t).length - 1 && <div className="block bg-secondary-text rounded-full w-1 h-1" />}
          </div>
        ))}
      </div>
      <div className="flex flex-row justify-between items-center gap-4">
        {environmentLabel && (
          <p data-testid="footer-environment" className="typo-secondary text-secondary-text">
            {environmentLabel}
          </p>
        )}
        {import.meta.env.VITE_ENV === 'dev' && canAccessDeveloperPage(roles, import.meta.env.VITE_ENV) && (
          <Button
            role="link"
            variant="ghost"
            data-testid="footer-dev-only-nav-button"
            onClick={handleDevPageNav}
            className="typo-secondary text-secondary-text"
          >
            <p data-testid="footer-dev-only-nav-text" className="typo-secondary text-secondary-text">
              {t.formatMessage(footerTranslations.devPage)}
            </p>
          </Button>
        )}

        {isAuthenticated && (
          <>
            <Suspense
              fallback={
                <p className="typo-secondary text-secondary-text px-2 py-1">
                  {t.formatMessage(footerTranslations.appVersion, { version: '-' })}
                </p>
              }
            >
              <AppVersion />
            </Suspense>
            <Suspense
              fallback={
                <p className="typo-secondary text-secondary-text px-2 py-1">
                  {t.formatMessage(upmVersionTranslations.upmVersion, { version: '-' })}
                </p>
              }
            >
              <UpmVersion />
            </Suspense>
          </>
        )}
        <p data-testid="footer-copyright-text" className="typo-secondary text-secondary-text">
          {t.formatMessage(footerTranslations.siemens, {
            year: new Date().getFullYear(),
          })}
        </p>
      </div>
    </div>
  );
};
